import { useEffect, useState } from "react";
import api from "../lib/api";
import { Button } from "./ui/button";
import { Loader2, MapPinned, Phone, AlertTriangle, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

const FIELD_LABELS = {
    email: "Email",
    cell_personale: "Cellulare",
    telefono_fisso: "Telefono fisso",
    indirizzo: "Indirizzo",
    cap: "CAP",
    citta: "Città",
    provincia: "Provincia",
};

const fmtDateTime = (iso) => {
    if (!iso) return "";
    try {
        return new Date(iso).toLocaleString("it-IT", {
            day: "2-digit", month: "2-digit", year: "numeric",
            hour: "2-digit", minute: "2-digit",
        });
    } catch { return iso; }
};

/**
 * Inline banner shown on the employee detail page when the employee has submitted
 * data updates that the admin has not yet acknowledged.
 *
 * Action: "Conferma e segna come letta" clears all pending change notifications
 * for THIS employee (one-shot mark-as-read).
 */
export const PendingChangesBanner = ({ employeeId }) => {
    const [pending, setPending] = useState([]);
    const [loading, setLoading] = useState(true);
    const [marking, setMarking] = useState(false);

    const fetchPending = async () => {
        if (!employeeId) return;
        setLoading(true);
        try {
            const { data } = await api.get(`/employees/${employeeId}/changes`, {
                params: { status: "new" },
            });
            setPending(data.items || []);
        } catch (e) {
            setPending([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchPending();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [employeeId]);

    const handleMarkRead = async () => {
        setMarking(true);
        try {
            const { data } = await api.post(`/employees/${employeeId}/changes/mark-read`);
            toast.success(`${data.updated} notific${data.updated === 1 ? "a" : "he"} segnata${data.updated === 1 ? "" : "e"} come letta${data.updated === 1 ? "" : "e"}`);
            setPending([]);
        } catch (e) {
            toast.error("Errore");
        } finally {
            setMarking(false);
        }
    };

    if (loading) return null;
    if (pending.length === 0) return null;

    const hasResidence = pending.some((p) => p.residence_changed);

    return (
        <section
            className="mb-4 rounded-lg border-2 border-amber-300 bg-amber-50 p-4 shadow-sm"
            data-testid="pending-changes-banner"
        >
            <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="flex items-start gap-3">
                    <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-amber-200 text-amber-800">
                        {hasResidence ? <MapPinned className="h-5 w-5" /> : <Phone className="h-5 w-5" />}
                    </div>
                    <div>
                        <div className="flex items-center gap-2 font-display text-sm font-bold text-amber-900">
                            <AlertTriangle className="h-4 w-4" />
                            {pending.length === 1
                                ? "Il dipendente ha aggiornato i propri dati"
                                : `Ci sono ${pending.length} aggiornamenti dati da rivedere`}
                        </div>
                        <p className="mt-0.5 text-xs text-amber-800">
                            Rivedi le modifiche qui sotto e conferma per chiudere la notifica.
                        </p>
                    </div>
                </div>
                <Button
                    onClick={handleMarkRead}
                    disabled={marking}
                    size="sm"
                    className="gap-2 bg-emerald-600 text-white hover:bg-emerald-700"
                    data-testid="mark-changes-read-btn"
                >
                    {marking ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
                    Conferma e segna come letta
                </Button>
            </div>

            <div className="mt-3 space-y-3 rounded-md bg-white/80 p-3 ring-1 ring-amber-200">
                {pending.map((item) => (
                    <div key={item.id} data-testid={`pending-change-${item.id}`}>
                        <div className="flex flex-wrap items-center gap-2 text-[11px] text-slate-500">
                            <span>Inviata il {fmtDateTime(item.created_at)}</span>
                            {item.residence_changed && (
                                <span className="inline-flex items-center gap-1 rounded-full border border-amber-300 bg-amber-100 px-2 py-0.5 font-semibold text-amber-800">
                                    <MapPinned className="h-3 w-3" />
                                    Cambio residenza · verifica documento ufficiale
                                </span>
                            )}
                        </div>
                        <ul className="mt-1.5 space-y-0.5 text-sm">
                            {(item.changes || []).map((c, idx) => (
                                <li key={c.field || idx} className="text-slate-700">
                                    <span className="font-medium text-slate-600">
                                        {FIELD_LABELS[c.field] || c.field}:
                                    </span>{" "}
                                    <span className="text-slate-400 line-through">{c.old || "—"}</span>
                                    {" → "}
                                    <span className="font-semibold text-emerald-700">{c.new || "—"}</span>
                                </li>
                            ))}
                        </ul>
                        {item.note && (
                            <p className="mt-1 max-w-2xl rounded-md bg-slate-50 px-2 py-1 text-xs italic text-slate-600">
                                "{item.note}"
                            </p>
                        )}
                    </div>
                ))}
            </div>
        </section>
    );
};
